/* ──────────────────────────────────────────────────────────────────────────
 * IPO Role Select — shown when a user clicks "Enter my IPO workspace".
 * Strict rule: the "Continue as …" buttons only work for EXISTING users who
 * already registered on that path (workspace / ca_firm). New users are forced
 * to "Create an account" / "Register your firm". Admins bypass all gating.
 * Top bar shows the logged-in user's identity; every CTA is a native anchor so
 * it can be opened in a new tab via right-click. "Contact Us" (lead dialog)
 * lives at the bottom. Scoped under `.irs`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getContext, setRole } from "@/lib/roleContext";
import ContactLeadDialog from "@/components/ContactLeadDialog";
import "./IPORoleSelect.css";

const IPORoleSelect = () => {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => { (async () => setCtx(await getContext()))(); }, []);

  const enterRole = async (role, path) => { await setRole(role); navigate(path); };

  // Left-click navigates in-place; right-click / modified-click falls through to
  // the native anchor so the destination opens in a new tab.
  const isModified = (e) => e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
  const navClick = (e, to) => {
    if (isModified(e)) return;
    e.preventDefault();
    navigate(to);
  };

  // Browser Back on this role-select page returns the user to their Home Page
  // (kept logged in — client-side SPA navigation, no session change).
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () => navigate("/home");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [navigate]);

  const isAdmin = !!ctx?.is_admin;
  const roleIds = (ctx?.roles || []).map((r) => r.id);
  const hasWorkspace = roleIds.includes("workspace");
  const hasCaFirm = roleIds.includes("ca_firm");
  const ceoLocked = !!ctx && !isAdmin && !hasWorkspace;
  const caLocked = !!ctx && !isAdmin && !hasCaFirm;

  const continueCeo = async (e) => {
    if (e && isModified(e)) return; // let native open-in-new-tab / ctrl-click work
    if (e) e.preventDefault();
    const c = ctx || (await getContext());
    if (c?.is_admin || (c?.roles || []).some((r) => r.id === "workspace")) {
      return enterRole("workspace", "/ipo-navigator");
    }
    toast.error("No founder account found yet — please create your account below to continue.");
  };

  const continueCa = async (e) => {
    if (e && isModified(e)) return;
    if (e) e.preventDefault();
    const c = ctx || (await getContext());
    if (c?.is_admin || (c?.roles || []).some((r) => r.id === "ca_firm")) {
      return enterRole("ca_firm", "/ca-firm-dashboard");
    }
    toast.error("No CA firm registered yet — please register your firm below to continue.");
  };

  const continueAdvisor = (e) => {
    if (e && isModified(e)) return;
    if (e) e.preventDefault();
    enterRole("advisor", "/advisor-onboarding");
  };

  const initials = (ctx?.name || "U").trim().charAt(0).toUpperCase() || "U";

  return (
    <div className="irs" data-testid="ipo-role-select-page">
      <div className="irs-top">
        <div className="irs-top-l">
          <a className="irs-brand" href="/home" onClick={(e) => navClick(e, "/home")} data-testid="irs-brand">IPO<span>Labs</span></a>
          <a className="irs-topbtn" href="/home" onClick={(e) => navClick(e, "/home")} data-testid="irs-home"><i className="ti ti-home" aria-hidden="true" /> Home</a>
          <a className="irs-topbtn" href="/oracle" onClick={(e) => navClick(e, "/oracle")} data-testid="irs-ask-oracle"><i className="ti ti-robot" aria-hidden="true" /> Ask Oracle {"{IPO.GPT}"}</a>
        </div>
        {ctx && (
          <div className="irs-user" data-testid="irs-user-chip">
            <div className="irs-user-av" aria-hidden="true">{initials}</div>
            <div className="irs-user-tx">
              <div className="irs-user-n" data-testid="irs-user-name">{ctx.name || "Signed in"}</div>
              <div className="irs-user-e" data-testid="irs-user-email">{ctx.email}</div>
            </div>
          </div>
        )}
      </div>

      <div className="irs-main">
        <div className="irs-card">
          <span className="irs-badge">TAKE YOUR COMPANY PUBLIC</span>
          <h1 className="irs-h">How would you like to continue?</h1>
          <p className="irs-sub">IPOLabs works differently depending on who&apos;s driving the IPO. Pick the path that matches you.</p>

          <div className="irs-grid">
            <div className="irs-opt" data-testid="irs-opt-ceo">
              <div className="irs-ic ceo"><i className="ti ti-rocket" aria-hidden="true" /></div>
              <div className="irs-opt-h">I&apos;m a CEO or founder</div>
              <div className="irs-opt-d">Track your own IPO readiness, hire verified advisors, and manage your DRHP-to-listing journey.</div>
              <a
                className={`irs-btn dark${ceoLocked ? " locked" : ""}`}
                href={ceoLocked ? "/ipo-workspace-onboarding" : "/ipo-navigator"}
                onClick={continueCeo}
                data-testid="irs-continue-ceo"
              >
                {ceoLocked && <i className="ti ti-lock" aria-hidden="true" />} Continue as CEO / founder
              </a>
              <div className="irs-new">New here? <a className="irs-link purple" href="/ipo-workspace-onboarding" onClick={(e) => navClick(e, "/ipo-workspace-onboarding")} data-testid="irs-create-account">Create an account</a></div>
            </div>

            <div className="irs-opt" data-testid="irs-opt-ca">
              <div className="irs-ic ca"><i className="ti ti-briefcase" aria-hidden="true" /></div>
              <div className="irs-opt-h">I&apos;m a CA firm or advisor</div>
              <div className="irs-opt-d">Rank your clients by IPO readiness, run financial assessments, and promote ready clients to IPOLabs.</div>
              <a
                className={`irs-btn line${caLocked ? " locked" : ""}`}
                href={caLocked ? "/ca-firm-onboarding" : "/ca-firm-dashboard"}
                onClick={continueCa}
                data-testid="irs-continue-ca"
              >
                {caLocked && <i className="ti ti-lock" aria-hidden="true" />} Continue as CA firm
              </a>
              <div className="irs-new">New here? <a className="irs-link green" href="/ca-firm-onboarding" onClick={(e) => navClick(e, "/ca-firm-onboarding")} data-testid="irs-register-firm">Register your firm</a></div>
            </div>

            <div className="irs-div" aria-hidden="true" />

            <div className="irs-opt" data-testid="irs-opt-advisor">
              <div className="irs-ic adv"><i className="ti ti-user-star" aria-hidden="true" /></div>
              <div className="irs-opt-h">I am an IPO Advisor</div>
              <div className="irs-opt-d">Register yourself or your firm on the Advisor Marketplace and get matched to MSMEs assembling their IPO teams.</div>
              <a className="irs-btn adv" href="/advisor-onboarding" onClick={continueAdvisor} data-testid="irs-continue-advisor">
                <i className="ti ti-rocket" aria-hidden="true" /> Continue as IPO Advisor
              </a>
              <div className="irs-new">New here? <a className="irs-link teal" href="/advisor-onboarding" onClick={continueAdvisor} data-testid="irs-register-advisor">Register as an advisor</a></div>
            </div>
          </div>

          <div className="irs-oracle" onClick={() => navigate("/oracle")} role="button" tabIndex={0} data-testid="irs-oracle-promo">
            <div className="irs-oracle-ic"><i className="ti ti-robot" aria-hidden="true" /></div>
            <div className="irs-oracle-tx">
              <div className="irs-oracle-h">Not sure which path is right for you?</div>
              <div className="irs-oracle-d">Ask <b>Oracle {"{IPO.GPT}"}</b> — India&apos;s IPO LLM. Get instant, cited answers on eligibility, costs, timelines &amp; DRHP.</div>
            </div>
            <button className="irs-oracle-btn" onClick={(e) => { e.stopPropagation(); navigate("/oracle"); }} data-testid="irs-oracle-btn">Ask Oracle {"{IPO.GPT}"} <i className="ti ti-arrow-right" aria-hidden="true" /></button>
          </div>

          <div className="irs-contact" data-testid="irs-contact-wrap">
            Still have questions?
            <button type="button" className="irs-contact-btn" onClick={() => setContactOpen(true)} data-testid="irs-contact-us">
              <i className="ti ti-headset" aria-hidden="true" /> Contact Us
            </button>
          </div>
        </div>
      </div>

      <ContactLeadDialog open={contactOpen} onOpenChange={setContactOpen} leadType="sales" />
    </div>
  );
};

export default IPORoleSelect;
